import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { OAuthProvider } from '@mondaygpt/constants'
import { useAnalytics } from 'hooks'
import { selectBusinessId } from 'modules/authentication'
import { useAppSelector } from 'modules/core'
import { oauthUtils } from 'modules/oauth/utils/oauth-utils'
import { useLinkedInConnect } from 'modules/oauth/hooks/useLinkedInConnect'
import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import { IntegrationsService } from '../../../openapi/services/IntegrationsService'
import { resolvePlatformPublishConfig } from './platform-publish-config'

type IntegrationStatus = Awaited<
  ReturnType<typeof IntegrationsService.integrationControllerGetIntegrationStatusV1>
>

interface UsePlatformPublishOptions {
  /** OAuth/integration provider key, e.g. 'twitter', 'linkedin', 'pinterest', 'tiktok', 'facebook'. */
  provider: string
  /** Creative id — used in analytics payloads. */
  creativeId: string
  /** Publish function called when the user is already connected. */
  publishFn: () => void | Promise<void>
  /**
   * Optional pre-flight check, runs before the integration status fetch.
   * Return `false` to abort (e.g. gate denied, content invalid, coming-soon).
   * Callers should fire SOCIAL_PUBLISH_TAPPED here when appropriate.
   */
  beforeStatusCheck?: () => boolean | Promise<boolean>
  /**
   * Determines whether an integration status counts as "connected and ready to publish".
   * Defaults to `status.isConnected`. Facebook needs `pageId`, Pinterest needs `defaultBoardId`.
   */
  isConnected?: (status: IntegrationStatus) => boolean
  /** Copy used in the "something went wrong" fallback error. Defaults to provider name. */
  providerDisplayName?: string
}

export interface UsePlatformPublishResult {
  /** Bind to the publish CTA button. Checks connection, opens dialog, or invokes publishFn. */
  handlePostClick: () => Promise<void>
  /** Bind to the connect CTA inside the dialog. Opens OAuth popup, verifies, retriggers publish. */
  handleConnect: () => Promise<void>
  isConnecting: boolean
  connectError: string | null
  showConnectDialog: boolean
  setShowConnectDialog: (open: boolean) => void
  /** Use this for the Dialog `onOpenChange` so dismissal analytics fire. */
  onDialogOpenChange: (open: boolean) => void
  /** The shared LinkedIn Personal/Company-Page chooser dialog. Render it in the consumer's JSX. */
  chooser: ReactNode
}

/**
 * Encapsulates the per-platform "click → check connection → either publish or open connect dialog
 * → on connect, retrigger publish" flow that previously appeared 5+ times in ComposerHeader.
 *
 * The hook owns the dialog open/error/connecting state and the OAuth/analytics orchestration.
 * It does NOT own the publish itself or any feature-gate / content-validation pre-checks —
 * those stay with the caller, exposed through `publishFn` and `beforeStatusCheck`.
 */
export function usePlatformPublish(options: UsePlatformPublishOptions): UsePlatformPublishResult {
  const {
    provider,
    creativeId,
    publishFn,
    beforeStatusCheck,
    isConnected = status => status.isConnected,
    providerDisplayName,
  } = options

  const { trackDialogViewed, trackDialogDismissed, trackConnectAnalytics, verifyAfterConnect } =
    resolvePlatformPublishConfig(provider as OAuthProvider)

  const { track } = useAnalytics()
  const businessId = useAppSelector(selectBusinessId)
  const { resolveConnectProvider, chooser } = useLinkedInConnect()

  const [showConnectDialog, setShowConnectDialog] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)

  const openConnectDialog = useCallback(() => {
    setShowConnectDialog(true)
    setConnectError(null)
    if (trackDialogViewed) {
      track(AnalyticsEvents.Social.SOCIAL_CONNECT_DIALOG_VIEWED, {
        platform: provider,
        creative_id: creativeId,
        source: 'composer',
      })
    }
  }, [trackDialogViewed, track, provider, creativeId])

  const handlePostClick = useCallback(async () => {
    if (beforeStatusCheck) {
      const ok = await beforeStatusCheck()
      if (!ok) return
    }

    try {
      const status = await IntegrationsService.integrationControllerGetIntegrationStatusV1(provider)
      if (isConnected(status)) {
        await publishFn()
      } else {
        openConnectDialog()
      }
    } catch {
      // Status check failed — surface the connect dialog as the safe fallback.
      // Note: when this branch is hit we deliberately do NOT fire the
      // SOCIAL_CONNECT_DIALOG_VIEWED event, matching the pre-refactor behavior
      // for TikTok / YouTube / Facebook / LinkedIn / X / Pinterest. Instagram
      // historically did fire it in the catch — that one was the odd one out
      // and is not migrated to this hook.
      setShowConnectDialog(true)
      setConnectError(null)
    }
  }, [beforeStatusCheck, provider, isConnected, publishFn, openConnectDialog])

  const handleConnect = useCallback(async () => {
    if (!businessId) return
    // LinkedIn fronts two OAuth apps (personal `linkedin` / company `linkedin_org`).
    // Open the shared chooser first (when gated on); null = dismissed → abort before
    // the spinner. Other providers pass through. Both LinkedIn variants land on the
    // single `linkedin` integration, so the status verify below stays keyed on `provider`.
    const oauthProvider = await resolveConnectProvider(provider)
    if (!oauthProvider) return
    setIsConnecting(true)
    setConnectError(null)
    try {
      const result = await oauthUtils.openOAuthPopup(oauthProvider, {
        businessId,
        source: 'composer',
        track,
      })
      if (result === false) {
        setConnectError('Connection was cancelled or failed. Please try again.')
        if (trackConnectAnalytics) {
          track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
            provider,
            source: 'composer',
            error: 'oauth_popup_failed',
            error_code: 'oauth_popup_failed',
            error_type: 'popup',
          })
        }
        setIsConnecting(false)
        return
      }

      if (!verifyAfterConnect) {
        // Facebook path: OAuth done, but the user still needs to pick a Page.
        // Just close the dialog and bail.
        setShowConnectDialog(false)
        setConnectError(null)
        return
      }

      // Bridge the ~800ms gap between popup self-close (localStorage path)
      // and final integration persistence. Otherwise the status check below
      // can return isConnected:false on a successful OAuth and we surface a
      // "Connection cancelled" error on a working flow.
      await oauthUtils.waitForOAuthPersistence(provider)

      try {
        const status =
          await IntegrationsService.integrationControllerGetIntegrationStatusV1(provider)
        if (isConnected(status)) {
          if (trackConnectAnalytics) {
            track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_SUCCESS, {
              provider,
              source: 'composer',
            })
          }
          setShowConnectDialog(false)
          await publishFn()
        } else {
          setConnectError('Connection was cancelled or failed. Please try again.')
          if (trackConnectAnalytics) {
            track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
              provider,
              source: 'composer',
              error: 'verification_not_connected',
              error_code: 'verification_not_connected',
              error_type: 'verification',
            })
          }
        }
      } catch {
        setConnectError('Could not verify connection. Please try again.')
        if (trackConnectAnalytics) {
          track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
            provider,
            source: 'composer',
            error: 'verification_check_failed',
            error_code: 'verification_check_failed',
            error_type: 'network',
          })
        }
      }
    } catch {
      setConnectError(`Something went wrong connecting to ${providerDisplayName ?? provider}.`)
      if (trackConnectAnalytics) {
        track(AnalyticsEvents.Chat.INTEGRATION_CONNECT_FAILED, {
          provider,
          source: 'composer',
          error: 'oauth_flow_error',
          error_code: 'oauth_flow_error',
          error_type: 'unknown',
        })
      }
    } finally {
      setIsConnecting(false)
    }
  }, [
    businessId,
    resolveConnectProvider,
    provider,
    isConnected,
    publishFn,
    track,
    trackConnectAnalytics,
    verifyAfterConnect,
    providerDisplayName,
  ])

  const onDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open && showConnectDialog && trackDialogDismissed) {
        track(AnalyticsEvents.Social.SOCIAL_CONNECT_DIALOG_DISMISSED, {
          platform: provider,
          creative_id: creativeId,
          source: 'composer',
        })
      }
      setShowConnectDialog(open)
    },
    [showConnectDialog, trackDialogDismissed, track, provider, creativeId]
  )

  return {
    handlePostClick,
    handleConnect,
    isConnecting,
    connectError,
    showConnectDialog,
    setShowConnectDialog,
    onDialogOpenChange,
    chooser,
  }
}
